import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import Popup from "../popup/Popup";
import { POPUP_FORM_CONTENT_CLASS } from "../popup/popupLayoutSpec";
import Heading from "../common/Typography/Heading";
import Paragraph from "../common/Typography/Paragraph";
import AuxiliaryText from "../common/Typography/AuxiliaryText";
import SpecialText from "../common/Typography/SpecialText";
import MainButton from "../common/MainButton/MainButton";
import StepBackButton from "../common/StepBackButton/StepBackButton";
import TextFiled from "../common/InputField/TextFiled";
import TextArea from "../common/InputField/TextArea";
import FieldDropdown from "../common/InputField/FieldDropdown";
import useAuth from "../../Hooks/useAuth";
import DropzoneUploader from "../common/DropzoneUploader/DropzoneUploader";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { disableScroll, enableScroll } from "../../utils/scrollUtils";
import { useGraphQL } from "../../context/GraphQLProvider";
import { 
  setField, 
  setStep, 
  setSubmitting, 
  setConfirmation, 
  setImages, 
  resetQuoteRequest 
} from "../../Redux/Slice/quoteRequestSlice";
import { CreateQuoteRequestMutation, transformQuoteRequestInput } from "../../services/QuoteRequestService";
import { runTextPipeline } from "../../utils/contentSafety";
import Alerts from "../common/Alerts/Alerts";
import Spinner from "../common/Spinner";



// Image Container Component
const ImageContainer = React.memo(({ url, name, isDragging }) => (
  <div className="relative size-full">
    <div 
      className="size-full bg-cover bg-center rounded-lg shadow-sm group-hover:shadow-md transition-all duration-200"
      style={{ 
        backgroundImage: `url(${url})`,
        opacity: isDragging ? 0.5 : 1
      }}
    />
    <div className="absolute inset-0 bg-transparent" />
  </div>
));

// Sortable Item Component
const SortableItem = ({ id, item, index, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = useMemo(() => ({
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : 'transform 200ms cubic-bezier(0.2, 0, 0, 1)',
    cursor: isDragging ? 'grabbing' : 'grab',
    touchAction: 'none',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    position: 'relative',
    zIndex: isDragging ? 1 : 'auto',
  }), [transform, transition, isDragging]);

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div className="relative group">
        <div className="size-[60px]">
          <ImageContainer 
            url={item.url || ''} 
            name={item?.file?.name || 'Uploaded image'}
            isDragging={isDragging}
          />
          <button 
            type="button" 
            onClick={() => onDelete(index)}
            className="absolute -top-2 -right-2 size-5 flex items-center justify-center bg-white rounded-full shadow-md hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
          >
            <i className="fi fi-rs-cross text-[10px] text-offblack"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

// Validation schema for the quote request form
const quoteRequestValidationSchema = Yup.object().shape({
  // Step 1: Job Location
  jobLocation: Yup.string()
    .min(5, 'Location must be at least 5 characters')
    .max(60, 'Location cannot exceed 60 characters')
    .required('Job location is required')
    .test('contentSafety', function (value) {
      if (!value || !String(value).trim()) return true;
      const result = runTextPipeline(value, 'location');
      if (result.valid) return true;
      return this.createError({ message: result.message });
    }),
  
  // Step 2: Job Title
  jobTitle: Yup.string()
    .min(5, 'Job title must be at least 5 characters')
    .max(60, 'Job title cannot exceed 60 characters')
    .required('Job title is required')
    .test('contentSafety', function (value) {
      if (!value || !String(value).trim()) return true;
      const result = runTextPipeline(value, 'jobTitle');
      if (result.valid) return true;
      return this.createError({ message: result.message });
    }),
  
  // Step 3: Job Details (quote context — pricing allowed)
  jobDescription: Yup.string()
    .min(5, 'Job details must be at least 5 characters')
    .max(1000, 'Job details cannot exceed 1000 characters')
    .required('Job details are required')
    .test('contentSafety', function (value) {
      if (!value || !String(value).trim()) return true;
      const result = runTextPipeline(value, 'quoteMessage');
      if (result.valid) return true;
      return this.createError({ message: result.message });
    }),
  
  // Step 4: Photos (Optional) - Now stored as stringified JSON
  photos: Yup.string().test('photos-validation', 'Maximum 5 photos allowed', function(value) {
    if (!value) return true; // Allow empty string
    try {
      const parsedPhotos = JSON.parse(value);
      return Array.isArray(parsedPhotos) && parsedPhotos.length <= 5;
    } catch {
      return false; // Invalid JSON
    }
  }),
  
  // Step 5: Job Type
  jobType: Yup.string()
    .oneOf(['repair', 'replacement', 'new_installation', 'maintenance', 'not_sure'], 'Please select a job type')
    .required('Job type is required'),
  
  // Step 6: Property Type
  propertyType: Yup.string()
    .oneOf(['house', 'apartment', 'townhouse', 'unit', 'duplex', 'commercial', 'other'], 'Please select a property type')
    .required('Property type is required'),
  
  // Step 7: Site Category
  sitePurpose: Yup.string()
    .oneOf(['residential', 'commercial'], 'Please select site category')
    .required('Site category is required'),
  
  // Step 8: Timeframe
  urgency: Yup.string()
    .oneOf(['urgently', 'this_week', 'next_week', 'flexible'], 'Please select timeframe')
    .required('Timeframe is required'),
  
  // Step 10: Contact preference
  directContact: Yup.boolean(),
  name: Yup.string().when('directContact', {
    is: true,
    then: Yup.string()
      .min(5, 'Full name must be at least 5 characters')
      .max(60, 'Full name cannot exceed 60 characters')
      .required('Full name is required')
      .test('contentSafety', function (value) {
        if (!value || !String(value).trim()) return true;
        const result = runTextPipeline(value, 'name');
        if (result.valid) return true;
        return this.createError({ message: result.message });
      })
  }),
  email: Yup.string().when('directContact', {
    is: true,
    then: Yup.string()
      .email('Please enter a valid email')
      .required('Email is required')
  }),
  mobile: Yup.string().when('directContact', {
    is: true,
    then: Yup.string()
      .matches(/^[0-9+\-\s()]+$/, 'Please enter a valid phone number')
      .min(8, 'Phone number must be at least 8 characters')
      .required('Mobile number is required')
  }),
});

const QuoteRequestForm = ({ isOpen, onClose }) => {
  const graphqlService = useGraphQL();
  const dispatch = useDispatch();
  const { auth } = useAuth();
  const [showCrossButton, setShowCrossButton] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localImages, setLocalImages] = useState([]); // Local state for UI display
  
  // Helper function to parse stringified photos and images
  const parseStringifiedData = useCallback((dataString) => {
    if (!dataString) return [];
    try {
      return JSON.parse(dataString);
    } catch {
      return [];
    }
  }, []);
  
  // Get state from Redux
  const quoteRequestState = useSelector((state) => state.quoteRequest);
  const {
    currentStep,
    isSubmitting,
    showConfirmation,
    images,
    jobTitle,
    jobType,
    jobLocation,
    propertyType,
    sitePurpose,
    urgency,
    jobDescription,
    photos,
    directContact,
    name,
    email,
    mobile,
    flowType,
    senderId
  } = quoteRequestState;

  // Job type options
  const jobTypeOptions = [
    { label: "Repair", value: "repair" },
    { label: "Replacement", value: "replacement" },
    { label: "New Installation", value: "new_installation" },
    { label: "Maintenance", value: "maintenance" },
    { label: "Not Sure", value: "not_sure" }
  ];

  // Property type options
  const propertyTypeOptions = [
    { label: "House", value: "house" },
    { label: "Apartment", value: "apartment" },
    { label: "Townhouse", value: "townhouse" },
    { label: "Unit", value: "unit" },
    { label: "Duplex", value: "duplex" },
    { label: "Commercial", value: "commercial" },
    { label: "Other", value: "other" }
  ];

  // Site purpose options
  const sitePurposeOptions = [
    { label: "Residential", value: "residential" },
    { label: "Commercial", value: "commercial" }
  ];

  // Timeframe options
  const urgencyOptions = [
    { label: "Urgent (1–2 days)", value: "urgently" },
    { label: "This Week", value: "this_week" },
    { label: "Next Week", value: "next_week" },
    { label: "Flexible", value: "flexible" }
  ];

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const formik = useFormik({
    initialValues: {
      jobTitle: jobTitle || "",
      jobType: jobType || "",
      jobLocation: jobLocation || "",
      propertyType: propertyType || "",
      sitePurpose: sitePurpose || "",
      urgency: urgency || "",
      jobDescription: jobDescription || "",
      photos: photos || "", // Initialize as empty string
      directContact: directContact !== null ? directContact : null,
      name: name || (auth?.firstName ? `${auth.firstName} ${auth.lastName || ''}`.trim() : ""),
      email: email || (auth?.personalEmail || ""),
      mobile: mobile || (auth?.primaryContactNumber || ""),
      flowType: flowType || "getfreequote",
      senderId: senderId || null,
    },
    validationSchema: quoteRequestValidationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      console.log("Quote Request Values:", values);
      dispatch(setSubmitting(true));
      
      try {
        // Bypass API for now - directly show confirmation
        await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API delay
        
        dispatch(setConfirmation(true));
        
        // Auto-close after 3 seconds
        setTimeout(() => {
          handleClose();
        }, 3000);
        
        /* TODO: Uncomment when ready to integrate API
        // Ensure photos are stringified before submission using local images
        const submissionValues = {
          ...values,
          photos: JSON.stringify(localImages)
        };
        
        // Transform data for GraphQL
        const quoteRequestInput = transformQuoteRequestInput(submissionValues, auth);
        
        // Submit to GraphQL API
        const response = await CreateQuoteRequestMutation(graphqlService, quoteRequestInput);
        
        if (response.createQuoteRequest?.id) {
          dispatch(setConfirmation(true));
          
          // Auto-close after 3 seconds
          setTimeout(() => {
            handleClose();
          }, 3000);
        }
        */
      } catch (error) {
        console.error('Error submitting quote request:', error);
        Alerts.error(error.message || 'Failed to submit quote request');
      } finally {
        dispatch(setSubmitting(false));
      }
    }
  });

  // Handle step navigation
  const handleNext = () => {
    if (currentStep < getMaxStep()) {
      dispatch(setStep(currentStep + 1));
    } else {
      formik.handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      dispatch(setStep(currentStep - 1));
    }
  };

  // Get maximum step
  const getMaxStep = () => {
    // If directContact is true: Intro (1) + Steps 2-10 + Name (11) + Email (12) + Mobile (13) + Submit (14) = 14 steps
    // If directContact is false: Intro (1) + Steps 2-10 + Submit (11) = 11 steps
    return formik.values.directContact ? 14 : 11;
  };

  // Handle checkbox changes
  const handleCheckboxChange = (fieldName, value) => {
    formik.setFieldValue(fieldName, value);
    dispatch(setField({ [fieldName]: value }));
    
    // Auto-fill contact details when user selects "Yes" for direct contact
    if (fieldName === 'directContact' && value === true && auth) {
      const userData = {
        name: auth.firstName ? `${auth.firstName} ${auth.lastName || ''}`.trim() : "",
        email: auth.personalEmail || "",
        mobile: auth.primaryContactNumber || "",
      };
      
      // Update formik values
      formik.setFieldValue('name', userData.name);
      formik.setFieldValue('email', userData.email);
      formik.setFieldValue('mobile', userData.mobile);
      
      // Update Redux state
      dispatch(setField(userData));
    }
  };

  // Handle text field change
  const handleTextFieldChange = (e) => {
    const { name, value } = e.target;
    formik.handleChange(e);
    dispatch(setField({ [name]: value }));
  };

  // Handle text field blur - validate field
  const handleTextFieldBlur = async (e) => {
    formik.handleBlur(e);
    // Validate the specific field
    const { name } = e.target;
    await formik.validateField(name);
  };

  // Handle dropdown change
  const handleDropdownChange = (fieldName, value) => {
    formik.setFieldValue(fieldName, value);
    dispatch(setField({ [fieldName]: value }));
  };

  // Handle image upload
  const handleUploadComplete = useCallback((uploadData) => {
    const newImages = Array.isArray(uploadData) ? uploadData : [uploadData];
    const processedImages = newImages.map((data, index) => ({
      name: data.fileName,
      url: data.url,
    }));
    
    const updatedImages = [...localImages, ...processedImages];
    
    // Update local state for UI
    setLocalImages(updatedImages);
    
    // Convert to stringified format for Redux
    const stringifiedImages = JSON.stringify(updatedImages);
    const stringifiedPhotos = JSON.stringify(updatedImages);
    
    dispatch(setImages(stringifiedImages));
    formik.setFieldValue("photos", stringifiedPhotos);
    dispatch(setField({ photos: stringifiedPhotos }));
  }, [localImages, formik, dispatch]);

  const deleteImage = useCallback((index) => {
    const newImages = localImages.filter((_, i) => i !== index);
    
    // Update local state for UI
    setLocalImages(newImages);
    
    // Convert to stringified format for Redux
    const stringifiedImages = JSON.stringify(newImages);
    const stringifiedPhotos = JSON.stringify(newImages);
    
    dispatch(setImages(stringifiedImages));
    formik.setFieldValue("photos", stringifiedPhotos);
    dispatch(setField({ photos: stringifiedPhotos }));
  }, [localImages, formik, dispatch]);

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const oldIndex = localImages.findIndex((_, index) => `item-${index}` === active.id);
      const newIndex = localImages.findIndex((_, index) => `item-${index}` === over.id);
      
      const newItems = arrayMove(localImages, oldIndex, newIndex);
      
      // Update local state for UI
      setLocalImages(newItems);
      
      // Convert to stringified format for Redux
      const stringifiedImages = JSON.stringify(newItems);
      const stringifiedPhotos = JSON.stringify(newItems);
      
      dispatch(setImages(stringifiedImages));
      formik.setFieldValue("photos", stringifiedPhotos);
      dispatch(setField({ photos: stringifiedPhotos }));
    }
    
    setIsDragging(false);
    setActiveId(null);
  }, [localImages, formik, dispatch]);

  const items = useMemo(() => 
    localImages.map((_, index) => `item-${index}`),
    [localImages]
  );

  // Get step title
  const getStepTitle = () => {
    switch (currentStep) {
      case 1: return "Request a Quote";
      case 2: return "Job Location";
      case 3: return "Job Title";
      case 4: return "Job Details";
      case 5: return "Photos";
      case 6: return "Job Type";
      case 7: return "Property Type";
      case 8: return "Site Category";
      case 9: return "Timeframe";
      case 10: return "Share contact details?";
      case 11: return formik.values.directContact ? "Full Name" : "Submit Request";
      case 12: return formik.values.directContact ? "Email" : "";
      case 13: return formik.values.directContact ? "Mobile" : "";
      case 14: return formik.values.directContact ? "Submit Request" : "";
      default: return "";
    }
  };

  // Check if current step is valid
  const isCurrentStepValid = () => {
    switch (currentStep) {
      case 1: return true; // Introduction step
      case 2: return !!formik.values.jobLocation && formik.values.jobLocation.length >= 5 && !formik.errors.jobLocation;
      case 3: return !!formik.values.jobTitle && formik.values.jobTitle.length >= 5 && !formik.errors.jobTitle;
      case 4: return !!formik.values.jobDescription && formik.values.jobDescription.length >= 5 && !formik.errors.jobDescription;
      case 5: return true; // Optional step - Photos
      case 6: return !!formik.values.jobType;
      case 7: return !!formik.values.propertyType;
      case 8: return !!formik.values.sitePurpose;
      case 9: return !!formik.values.urgency;
      case 10: return typeof formik.values.directContact === 'boolean';
      case 11: return formik.values.directContact ? (!!formik.values.name && formik.values.name.length >= 5 && !formik.errors.name) : true; // Submit Request if directContact is false
      case 12: return formik.values.directContact ? (!!formik.values.email && !formik.errors.email) : true;
      case 13: return formik.values.directContact ? (!!formik.values.mobile && formik.values.mobile.length >= 8 && !formik.errors.mobile) : true;
      case 14: return formik.values.directContact ? true : true; // Submit Request step
      default: return false;
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1: return renderIntroductionStep();
      case 2: return renderJobLocationStep();
      case 3: return renderJobTitleStep();
      case 4: return renderJobDescriptionStep();
      case 5: return renderPhotosStep();
      case 6: return renderJobTypeStep();
      case 7: return renderPropertyTypeStep();
      case 8: return renderSitePurposeStep();
      case 9: return renderUrgencyStep();
      case 10: return renderDirectContactStep();
      case 11: return formik.values.directContact ? renderNameStep() : renderSubmitRequestStep();
      case 12: return formik.values.directContact ? renderEmailStep() : null;
      case 13: return formik.values.directContact ? renderMobileStep() : null;
      case 14: return formik.values.directContact ? renderSubmitRequestStep() : null;
      default: return null;
    }
  };

  // Step 1: Introduction
  const renderIntroductionStep = () => (
    <div className="w-full">
      <div className="mb-6">
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-white rounded p-4 text-center">
            <div className="text-primaryblue mb-2">
              <i className="fi fi-rr-paper-plane text-xl"></i>
            </div>
            <Paragraph variant="BodyMeta" className="text-offblack">
              Sent to up to 5 local businesses
            </Paragraph>
          </div>
          
          <div className="bg-white rounded p-4 text-center">
            <div className="text-primaryblue mb-2">
              <i className="fi fi-rr-inbox text-xl"></i>
            </div>
            <Paragraph variant="BodyMeta" className="text-offblack">
              Replies appear in your Inbox
            </Paragraph>
          </div>
          
          <div className="bg-white rounded p-4 text-center">
            <div className="text-primaryblue mb-2">
              <i className="fi fi-rr-user-add text-xl"></i>
            </div>
            <Paragraph variant="BodyMeta" className="text-offblack">
              You may choose to share contact details
            </Paragraph>
          </div>
          
          <div className="bg-white rounded p-4 text-center">
            <div className="text-primaryblue mb-2">
              <i className="fi fi-rr-shield-check text-xl"></i>
            </div>
            <Paragraph variant="BodyMeta" className="text-offblack">
              Your contact details will not be public
            </Paragraph>
          </div>
        </div>
      
        <div className="text-center">
          <Paragraph variant="BodyMeta">
            By continuing, you agree to our{' '}
            <span className="text-primaryblue underline cursor-pointer">Privacy Policy</span>
            {' '}and{' '}
            <span className="text-primaryblue underline cursor-pointer">Terms of Use</span>.
          </Paragraph>
        </div>
      </div>
    </div>
  );

  // Step 2: Job Location
  const renderJobLocationStep = () => (
    <div className="w-full">
      <TextFiled
        variant="medium"
        fullWidth
        placeholder="Suburb or Postcode"
        name="jobLocation"
        value={formik.values.jobLocation}
        onChange={handleTextFieldChange}
        onBlur={handleTextFieldBlur}
        error={formik.touched.jobLocation && formik.errors.jobLocation ? formik.errors.jobLocation : null}
      />
    </div>
  );

  // Step 3: Job Title
  const renderJobTitleStep = () => (
    <div className="w-full">
      <TextFiled
        variant="medium"
        fullWidth
        placeholder='e.g. "Repair leaking shower"'
        name="jobTitle"
        value={formik.values.jobTitle}
        onChange={handleTextFieldChange}
        onBlur={handleTextFieldBlur}
        error={formik.touched.jobTitle && formik.errors.jobTitle ? formik.errors.jobTitle : null}
      />
    </div>
  );

  // Step 6: Job Type
  const renderJobTypeStep = () => (
    <div className="w-full">
      <FieldDropdown
        variant="medium"
        fullWidth
        value={formik.values.jobType}
        onChange={(e) => handleDropdownChange('jobType', e.value)}
        onBlur={() => formik.setFieldTouched('jobType', true)}
        options={jobTypeOptions}
        optionLabel="label"
        optionValue="value"
        placeholder="Select job type"
        error={formik.touched.jobType && formik.errors.jobType ? formik.errors.jobType : null}
      />
    </div>
  );


  // Step 7: Property Type
  const renderPropertyTypeStep = () => (
    <div className="w-full">
      <FieldDropdown
        variant="medium"
        fullWidth
        value={formik.values.propertyType}
        onChange={(e) => handleDropdownChange('propertyType', e.value)}
        onBlur={() => formik.setFieldTouched('propertyType', true)}
        options={propertyTypeOptions}
        optionLabel="label"
        optionValue="value"
        placeholder="Select property type"
        error={formik.touched.propertyType && formik.errors.propertyType ? formik.errors.propertyType : null}
      />
    </div>
  );

  // Step 8: Site Category
  const renderSitePurposeStep = () => (
    <div className="w-full">
      <FieldDropdown
        variant="medium"
        fullWidth
        value={formik.values.sitePurpose}
        onChange={(e) => handleDropdownChange('sitePurpose', e.value)}
        onBlur={() => formik.setFieldTouched('sitePurpose', true)}
        options={sitePurposeOptions}
        optionLabel="label"
        optionValue="value"
        placeholder="Select site category"
        error={formik.touched.sitePurpose && formik.errors.sitePurpose ? formik.errors.sitePurpose : null}
      />
    </div>
  );

  // Step 9: Timeframe
  const renderUrgencyStep = () => (
    <div className="w-full">
      <FieldDropdown
        variant="medium"
        fullWidth
        value={formik.values.urgency}
        onChange={(e) => handleDropdownChange('urgency', e.value)}
        onBlur={() => formik.setFieldTouched('urgency', true)}
        options={urgencyOptions}
        optionLabel="label"
        optionValue="value"
        placeholder="Select timeframe"
        error={formik.touched.urgency && formik.errors.urgency ? formik.errors.urgency : null}
      />
    </div>
  );

  // Step 4: Job Details
  const renderJobDescriptionStep = () => (
    <div className="w-full">
      <div className="relative">
        <TextArea
          variant="general"
          fullWidth
          placeholder='e.g. "Leak behind vanity. Tiled wall. Limited access."'
          name="jobDescription"
          value={formik.values.jobDescription}
          onChange={handleTextFieldChange}
          onBlur={handleTextFieldBlur}
          className="min-h-[120px] resize-none pr-16"
          style={{ resize: 'none' }}
        />
      </div>
      {formik.touched.jobDescription && formik.errors.jobDescription && (
        <SpecialText variant="FormValidation" className="text-alertred mt-2">
          {formik.errors.jobDescription}
        </SpecialText>
      )}
    </div>
  );

  // Step 5: Photos
  const renderPhotosStep = () => (
    <div className="w-full">
      <div className="w-full">
        {/* Uploaded Images */}
        {localImages.length > 0 && (
          <div className="mt-1 mb-5 w-full">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={items}
                strategy={rectSortingStrategy}
              >
                <div className="flex flex-wrap gap-7">
                  {localImages.map((item, index) => (
                    <SortableItem
                      key={`item-${index}`}
                      id={`item-${index}`}
                      item={item}
                      index={index}
                      onDelete={deleteImage}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        )}

        <DropzoneUploader
          isImage={true}
          maxFiles={5}
          loadingType="progress"
          onUploadComplete={handleUploadComplete}
          currentFileCount={localImages.length}
          applyFieldStyles={false}
          fullWidth
        />
      </div>
      <div className="mt-3">
        <AuxiliaryText variant="BodyMeta" className="text-mediumgray">
          Max 5 images • JPG / PNG / HEIC • Max 5MB per file
        </AuxiliaryText>
      </div>
    </div>
  );

  // Step 10: Direct Contact
  const renderDirectContactStep = () => (
    <div className="w-full">
      <div className="mb-6 flex flex-col gap-4">
        <div
          onClick={() => handleCheckboxChange('directContact', true)}
          className={`rounded-[4px] flex items-center justify-center w-full h-[48px] p-4 cursor-pointer border border-solid transition-colors duration-200 ${formik.values.directContact === true ? 'bg-primaryblue text-white border-primaryblue' : 'bg-white text-offblack border-[#C0C0C0]'}`}
        >
          <span className="input-label font-inter text-inherit text-left w-full">
            Yes, I want businesses to contact me directly
          </span>
        </div>
        <div
          onClick={() => handleCheckboxChange('directContact', false)}
          className={`rounded-[4px] flex items-center justify-center w-full h-[48px] p-4 cursor-pointer border border-solid transition-colors duration-200 ${formik.values.directContact === false ? 'bg-primaryblue text-white border-primaryblue' : 'bg-white text-offblack border-[#C0C0C0]'}`}
        >
          <span className="input-label font-inter text-inherit text-left w-full">
            No, contact me only through Inbox
          </span>
        </div>
      </div>
      <AuxiliaryText variant="BodyMeta" className="text-mediumgray">
        We'll include your contact info with your quote request.
      </AuxiliaryText>
    </div>
  );

  // Step 11: Name (if directContact is true)
  const renderNameStep = () => (
    <div className="w-full">
      <TextFiled
        variant="medium"
        fullWidth
        placeholder="Enter your full name"
        name="name"
        value={formik.values.name}
        onChange={handleTextFieldChange}
        onBlur={handleTextFieldBlur}
        error={formik.touched.name && formik.errors.name ? formik.errors.name : null}
      />
    </div>
  );

  // Step 12: Email (if directContact is true)
  const renderEmailStep = () => (
    <div className="w-full">
      <TextFiled
        variant="medium"
        fullWidth
        type="email"
        placeholder="Enter your email address"
        name="email"
        value={formik.values.email}
        onChange={handleTextFieldChange}
        onBlur={handleTextFieldBlur}
        error={formik.touched.email && formik.errors.email ? formik.errors.email : null}
      />
    </div>
  );

  // Step 13: Mobile (if directContact is true)
  const renderMobileStep = () => (
    <div className="w-full">
      <TextFiled
        variant="medium"
        fullWidth
        type="tel"
        placeholder="Enter your mobile number"
        name="mobile"
        value={formik.values.mobile}
        onChange={handleTextFieldChange}
        onBlur={handleTextFieldBlur}
        error={formik.touched.mobile && formik.errors.mobile ? formik.errors.mobile : null}
      />
    </div>
  );

  // Submit Request Step (Step 11 if directContact is false, Step 14 if directContact is true)
  const renderSubmitRequestStep = () => (
    <div className="w-full">
      <div className="text-center space-y-4">
        <Paragraph variant="BodyCompact" className="text-offblack">
          Your request will be sent to up to 5 local businesses.
        </Paragraph>
      </div>
    </div>
  );

  // Handle popup close
  const handleClose = () => {
    onClose();
    formik.resetForm();
    dispatch(resetQuoteRequest());
    // Reset local images state
    setLocalImages([]);
  };



  // Initialize form with user data when component mounts
  useEffect(() => {
    if (isOpen && auth) {
      const userData = {
        name: auth.firstName ? `${auth.firstName} ${auth.lastName || ''}`.trim() : "",
        email: auth.email || "",
        mobile: auth.primaryContactNumber || "",
        senderId: auth.user || null,
      };
      dispatch(setField(userData));
      
      // Initialize local images from stringified images in Redux
      if (images && typeof images === 'string') {
        try {
          const parsedImages = parseStringifiedData(images);
          if (Array.isArray(parsedImages)) {
            setLocalImages(parsedImages);
          }
        } catch (error) {
          console.warn('Failed to parse images:', error);
        }
      }
      
      // Also initialize from photos if images is empty
      if ((!images || !images.length) && photos && typeof photos === 'string') {
        try {
          const parsedPhotos = parseStringifiedData(photos);
          if (Array.isArray(parsedPhotos)) {
            setLocalImages(parsedPhotos);
            // Update images in Redux as well
            dispatch(setImages(JSON.stringify(parsedPhotos)));
          }
        } catch (error) {
          console.warn('Failed to parse photos:', error);
        }
      }
    }
  }, [isOpen, auth, dispatch, photos, images, parseStringifiedData]);

  // Update body overflow when popup is open
  useEffect(() => {
    if (isOpen) {
      disableScroll();
    } else {
      enableScroll();
    }
  }, [isOpen]);

  // Cleanup effect to ensure scrolling is restored on unmount
  useEffect(() => {
    return () => {
      enableScroll();
    };
  }, []);

  if (!isOpen) return null;

  // Render confirmation step
  if (showConfirmation) {
    return (
      <Popup
        setTrigger={() => handleClose()}
        ShowCrossButton={showCrossButton}
        onClose={handleClose}
        higherZIndex={true}
      >
        <div className="overflow-auto relative flex items-center justify-center" style={{ minHeight: 'calc(100vh - 120px)' }}>
          <div className={POPUP_FORM_CONTENT_CLASS}>
            <Heading
              variant={"h6"}
              className={
                "flex items-center justify-center text-center text-offblack"
              }
            >
              Quote Request Submitted!
            </Heading>
            <div className="mt-10 xl:mt-8 w-full">
              <div className="flex justify-center w-full">
                <div className="w-full mx-auto">
                  <div className="text-center space-y-4">
                    <Paragraph variant="MainParagraph" className="text-offblack">
                      Submitted. Expect replies within 24–48 hours.
                    </Paragraph>
                    {directContact && (
                      <Paragraph variant="MainParagraph" className="text-offblack mt-4">
                        If you allowed direct contact, businesses may also call or email you.
                      </Paragraph>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Popup>
    );
  }

  return (
    <Popup
      setTrigger={() => handleClose()}
      ShowCrossButton={showCrossButton}
      onClose={handleClose}
      className="text-offblack"
      higherZIndex={true}
    >

      {isSubmitting && <Spinner />}
      <div className="overflow-auto relative flex items-center justify-center" style={{ minHeight: 'calc(100vh - 120px)' }}>
        <div className={POPUP_FORM_CONTENT_CLASS}>
          <Heading variant="h6" className="flex items-center justify-center text-center">
            {getStepTitle()}
          </Heading>
          <div className="mt-10 xl:mt-8 w-full">
            {renderStepContent()}
            <div className={`flex items-center mt-10 w-full ${currentStep === 1 ? 'justify-center' : 'justify-between'}`}>
              {currentStep > 1 && <StepBackButton onClick={handleBack} />}
              <MainButton
                variant={(currentStep === 1 || currentStep === getMaxStep()) ? "medium" : "small"}
                onClick={handleNext}
                disabled={!isCurrentStepValid() || isSubmitting}
              >
                {(() => {
                  if (currentStep === 1) return 'Start My Quote';
                  const isSubmitStep = currentStep === getMaxStep();
                  return isSubmitStep ? (isSubmitting ? 'Submit My Quote' : 'Submit My Quote') : 'Next';
                })()}
              </MainButton>
            </div>
          </div>
        </div>
      </div>
    </Popup>
  );
};

export default QuoteRequestForm; 